import type { FieldTree } from '@angular/forms/signals';
import type { FactoryForm } from '@indutrust/factory-model';
import type { FactorySection } from './factory-section-status.util';

/**
 * Maps each factory editor section to the ordered list of form fields it owns.
 *
 * Used by {@link markSectionFieldsTouched} and
 * {@link focusFirstInvalidFieldInSection} to iterate only the fields that
 * belong to the section being acted on.
 */
const SECTION_FIELDS: Record<FactorySection, readonly (keyof FactoryForm)[]> = {
  general: ['name', 'website', 'description'],
  location: [
    'country_id',
    'industrial_area_id',
    'address',
    'latitude',
    'longitude',
  ],
  capabilities: [],
  machinePark: [],
  business: [
    'headcount_management',
    'headcount_production',
    'workshop_used',
    'workshop_available',
    'turnover',
    'trade_license_number',
    'certification',
  ],
  projectReferences: [],
  documents: [],
  gallery: [],
  virtualTour: [],
  contacts: ['contact_ids'],
};

/**
 * Marks all form fields belonging to the given section as touched so that
 * their bound `idt-input` / `idt-select` components show inline error messages.
 *
 * `idt-input.showError` gates on `errorMessage && touched`, so
 * calling this before rendering the section guarantees errors are visible
 * immediately after `onSave()` expands the card.
 *
 * @example
 * markSectionFieldsTouched('location', editFormStore.entityForm);
 */
export function markSectionFieldsTouched(
  section: FactorySection,
  entityForm: FieldTree<FactoryForm>
): void {
  for (const field of SECTION_FIELDS[section]) {
    entityForm[field]().markAsTouched();
  }
}

/**
 * Focuses the first form field in the given section that currently has
 * validation errors, using the Angular signal forms
 * {@link FieldState.focusBoundControl} API (no DOM query needed).
 *
 * Call this after {@link markSectionFieldsTouched} and after the section
 * card has been expanded so the bound control is visible in the DOM.
 *
 * @example
 * focusFirstInvalidFieldInSection('location', editFormStore.entityForm);
 */
export function focusFirstInvalidFieldInSection(
  section: FactorySection,
  entityForm: FieldTree<FactoryForm>
): void {
  for (const field of SECTION_FIELDS[section]) {
    const fieldState = entityForm[field]();
    if (fieldState.errors().length > 0) {
      fieldState.focusBoundControl();
      return;
    }
  }
}
